// React Query Configuration - Replaces complex Recoil state management
import { QueryClient } from '@tanstack/react-query';

// Default query configuration
const queryConfig = {
  defaultOptions: {
    queries: {
      // Stale time - data is considered fresh for this duration
      // Set to 0 so invalidation always triggers refetch
      staleTime: 0,

      // Cache time - how long to keep unused data in cache
      cacheTime: 5 * 60 * 1000, // 5 minutes

      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },

      // Refetch configuration
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,

      // Error handling
      onError: (error) => {
        console.error('Query error:', error);
      },
    },
    mutations: {
      // Retry configuration for mutations
      retry: (failureCount, error) => {
        // Don't retry mutations on client errors
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          return false;
        }
        return failureCount < 2;
      },

      // Error handling
      onError: (error) => {
        console.error('Mutation error:', error);
      },
    },
  },
};

// Create query client instance
export const queryClient = new QueryClient(queryConfig);

// Query keys factory - centralized query key management
export const queryKeys = {
  // Task-related queries
  tasks: {
    all: ['tasks'],
    lists: () => [...queryKeys.tasks.all, 'list'],
    list: (filters) => [...queryKeys.tasks.lists(), filters],
    details: () => [...queryKeys.tasks.all, 'detail'],
    detail: (id) => [...queryKeys.tasks.details(), id],
    trees: () => [...queryKeys.tasks.all, 'tree'],
    tree: (id) => [...queryKeys.tasks.trees(), id],
    stats: () => [...queryKeys.tasks.all, 'stats'],
  },

  // Idea-related queries
  ideas: {
    all: ['ideas'],
    lists: () => [...queryKeys.ideas.all, 'list'],
    list: (filters) => [...queryKeys.ideas.lists(), filters],
    details: () => [...queryKeys.ideas.all, 'detail'],
    detail: (id) => [...queryKeys.ideas.details(), id],
    progress: (id) => [...queryKeys.ideas.all, 'progress', id],
    plans: (id) => [...queryKeys.ideas.all, 'plans', id],
  },

  // Plan context queries (from Contex)
  plans: {
    all: ['plans'],
    contexts: () => [...queryKeys.plans.all, 'context'],
    context: (id) => [...queryKeys.plans.contexts(), id],
    details: () => [...queryKeys.plans.all, 'detail'],
    detail: (id) => [...queryKeys.plans.details(), id],
  },

  // Roadmap-related queries
  roadmaps: {
    all: ['roadmaps'],
    lists: (ideaId) => ['roadmaps', 'list', ideaId],
    list: (ideaId) => ['roadmaps', 'list', ideaId],
    details: () => ['roadmaps', 'detail'],
    detail: (ideaId, roadmapId) => ['roadmaps', 'detail', ideaId, roadmapId],
  },

  // User-related queries
  user: {
    all: ['user'],
    profile: () => [...queryKeys.user.all, 'profile'],
    preferences: () => [...queryKeys.user.all, 'preferences'],
  },

  // Auth-related queries
  auth: {
    all: ['auth'],
    session: () => [...queryKeys.auth.all, 'session'],
  },
};

// Utility function to invalidate related queries
export const invalidateQueries = {
  tasks: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all }),
    list: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.lists() }),
    detail: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) }),
    tree: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.tree(id) }),
    stats: () => queryClient.invalidateQueries({ queryKey: queryKeys.tasks.stats() }),
  },
  ideas: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.ideas.all }),
    list: () => queryClient.invalidateQueries({ queryKey: queryKeys.ideas.lists() }),
    detail: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(id) }),
    progress: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.ideas.progress(id) }),
    plans: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.ideas.plans(id) }),
  },
  plans: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.plans.all }),
    context: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.plans.context(id) }),
    detail: (id) => queryClient.invalidateQueries({ queryKey: queryKeys.plans.detail(id) }),
  },
  roadmaps: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.roadmaps.all }),
    list: (ideaId) => queryClient.invalidateQueries({ queryKey: queryKeys.roadmaps.list(ideaId) }),
    detail: (ideaId, roadmapId) => queryClient.invalidateQueries({ queryKey: queryKeys.roadmaps.detail(ideaId, roadmapId) }),
  },
  user: {
    all: () => queryClient.invalidateQueries({ queryKey: queryKeys.user.all }),
    profile: () => queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() }),
  },
};

export default queryClient;
